/** Runtime decoding primitives: every shape is checked, an unknown shape fails closed (ruling 0.3). */

/** Thrown when a server document does not have the shape the contract declares. */
export class ContractViolation extends Error {
  readonly path: string;

  constructor(path: string, expected: string) {
    super(`contract violation at ${path}: expected ${expected}`);
    this.name = "ContractViolation";
    this.path = path;
  }
}

/** A reader turns an unknown value at a path into a typed one or throws `ContractViolation`. */
export type Reader<T> = (value: unknown, path: string) => T;

const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const DIGITS = /^-?\d+$/;

/** Read a plain JSON object. */
export function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ContractViolation(path, "an object");
  }
  return value as Record<string, unknown>;
}

/** Read an object whose keys are exactly `keys` (no more, no fewer). */
export function exact(
  value: unknown,
  path: string,
  keys: readonly string[],
): Record<string, unknown> {
  const found = record(value, path);
  for (const key of keys) {
    if (!(key in found)) {
      throw new ContractViolation(`${path}.${key}`, "a present field");
    }
  }
  for (const key of Object.keys(found)) {
    if (!keys.includes(key)) {
      throw new ContractViolation(`${path}.${key}`, "no such field");
    }
  }
  return found;
}

/** Read the field `key` of an object already checked with `exact`. */
export function field<T>(
  found: Record<string, unknown>,
  path: string,
  key: string,
  read: Reader<T>,
): T {
  return read(found[key], `${path}.${key}`);
}

/** Read a string. */
export function str(value: unknown, path: string): string {
  if (typeof value !== "string") {
    throw new ContractViolation(path, "a string");
  }
  return value;
}

/** Read a safe integer (a float or a non-finite number is refused). */
export function int(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new ContractViolation(path, "an integer");
  }
  return value;
}

/** Read a non-negative safe integer. */
export function nat(value: unknown, path: string): number {
  const found = int(value, path);
  if (found < 0) {
    throw new ContractViolation(path, "a non-negative integer");
  }
  return found;
}

/** Read a boolean. */
export function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") {
    throw new ContractViolation(path, "a boolean");
  }
  return value;
}

/** Read a money amount in integer paise: a JSON integer or a digit string, as a `bigint`. */
export function paise(value: unknown, path: string): bigint {
  if (typeof value === "number" && Number.isSafeInteger(value)) {
    return BigInt(value);
  }
  if (typeof value === "string" && DIGITS.test(value)) {
    return BigInt(value);
  }
  throw new ContractViolation(path, "integer paise (an integer or a digit string)");
}

/** Make a reader for a derived decimal written as a string of exactly `scale` fractional digits. */
export function decimal(scale: number): Reader<string> {
  const shape = new RegExp(scale === 0 ? "^-?\\d+$" : `^-?\\d+\\.\\d{${String(scale)}}$`);
  return (value, path) => {
    if (typeof value !== "string" || !shape.test(value)) {
      throw new ContractViolation(path, `a decimal string of scale ${String(scale)}`);
    }
    return value;
  };
}

/** Read a UTC instant written as `YYYY-MM-DDTHH:MM:SS.ffffffZ`. */
export function instant(value: unknown, path: string): string {
  const text = str(value, path);
  if (!INSTANT.test(text)) {
    throw new ContractViolation(path, "a UTC instant with microseconds");
  }
  return text;
}

/** Make a reader accept `null` as well. */
export function nullable<T>(read: Reader<T>): Reader<T | null> {
  return (value, path) => (value === null ? null : read(value, path));
}

/** Make a reader for an array of members. */
export function listOf<T>(read: Reader<T>): Reader<T[]> {
  return (value, path) => {
    if (!Array.isArray(value)) {
      throw new ContractViolation(path, "an array");
    }
    return (value as unknown[]).map((member, index) => read(member, `${path}[${String(index)}]`));
  };
}

/** Make a reader for a string that is one of `allowed`. */
export function oneOf<const T extends readonly string[]>(allowed: T): Reader<T[number]> {
  return (value, path) => {
    const text = str(value, path);
    const found = allowed.find((candidate) => candidate === text);
    if (found === undefined) {
      throw new ContractViolation(path, `one of ${allowed.join(", ")}`);
    }
    return found;
  };
}

/** Make a reader for an object mapping any key to a version-like integer. */
export function intMap(value: unknown, path: string): ReadonlyMap<string, number> {
  const found = record(value, path);
  return new Map(
    Object.entries(found).map(([key, member]) => [key, nat(member, `${path}.${key}`)]),
  );
}
